import Agent from "infrastructure/agent";

import {
  CardMapper,
  DummyCardMapper,
  CardMoveMapper,
  EmployeeMapper,
  WorkScheduleMapper,
  WorkScheduleOverrideMapper,
  DeploymentDetailMapper,
  WorkScheduleQueryMapper,
} from "features/planner/planner.mappers";
import appService from "services/app.service";

/**
 * WorkSchedule
 */
const getEmployeeWorkSchedules = cardId =>
  Agent.get(`/card/${cardId}/employee/workschedules`);

const getEmployeeWorkSchedule = (cardId, workScheduleId) =>
  Agent.get(`/card/${cardId}/employee/workschedules/${workScheduleId}`);

const createEmployeeWorkSchedule = (cardId, workSchedule) =>
  Agent.post(
    `/card/${cardId}/employee/workschedules`,
    WorkScheduleMapper.to(workSchedule),
  );

const updateEmployeeWorkSchedule = (cardId, workScheduleId, workSchedule) =>
  Agent.put(
    `/card/${cardId}/employee/workschedules/${workScheduleId}`,
    WorkScheduleMapper.to(workSchedule),
  );

const deleteEmployeeWorkSchedule = (cardId, workScheduleId) =>
  Agent.delete(`/card/${cardId}/employee/workschedules/${workScheduleId}`);

const queryEmployeeWorkSchedule = (cardId, workScheduleQuery) =>
  Agent.post(`/card/${cardId}/employee/workschedules/query`, workScheduleQuery);

const queryEmployeeWorkSchedules = (cardId, startDate, endDate) =>
  Agent.post(
    `/card/${cardId}/employee/workschedules/queryall`,
    WorkScheduleQueryMapper.to({ startDate, endDate }),
  );

/**
 * WorkScheduleDefinition
 */
const getWorkScheduleDefinitions = () =>
  Agent.get(`/card/workscheduledefinitions`);

/**
 * WorkScheduleOverride
 */
const getWorkScheduleOverrides = cardId =>
  Agent.get(`/card/${cardId}/employee/workscheduleoverrides`);

const createWorkScheduleOverride = (cardId, workScheduleOverride) =>
  Agent.post(
    `/card/${cardId}/employee/workscheduleoverrides`,
    WorkScheduleOverrideMapper.to(workScheduleOverride),
  );

const updateWorkScheduleOverride = (
  cardId,
  workScheduleOverrideId,
  workScheduleOverride,
) =>
  Agent.put(
    `/card/${cardId}/employee/workscheduleoverrides/${workScheduleOverrideId}`,
    WorkScheduleOverrideMapper.to(workScheduleOverride),
  );

const deleteWorkScheduleOverride = (cardId, workScheduleOverrideId) =>
  Agent.delete(
    `/card/${cardId}/employee/workscheduleoverrides/${workScheduleOverrideId}`,
  );

/**
 * Card
 */
const getCard = cardId => Agent.get(`/card/${cardId}`);

const getCardsForGroups = groupIds =>
  Agent.post(`/card/bygroups`, groupIds.toJS());

const createCard = card => Agent.post(`/card`, CardMapper.to(card));

const createDummyCard = dummyCard =>
  Agent.post(`/card`, DummyCardMapper.to(dummyCard));

const updateDummyCard = (cardId, dummyCard) =>
  Agent.put(`/card/${cardId}`, CardMapper.to(dummyCard));

const deleteDummyCard = cardId => Agent.delete(`/card/${cardId}`);

const updateEmployee = (cardId, employee) =>
  Agent.put(`/card/${cardId}/employee`, EmployeeMapper.to(employee));

const hideEmployee = cardId => Agent.post(`/card/${cardId}/employee/hide`);

const showEmployee = cardId => Agent.post(`/card/${cardId}/employee/show`);

const assignProjectToEmployee = (cardId, projectId) =>
  Agent.put(`/card/${cardId}/employee/assign-project/${projectId}`);

const unassignProjectToEmployee = (cardId, projectId) =>
  Agent.put(`/card/${cardId}/employee/unassign-project/${projectId}`);

const getEmployeesProjects = cardId => Agent.get(`/project/bycard/${cardId}`);

const getCardSpecificationGapReportForGroup = (groupId, cardId) =>
  Agent.get(`/card/${cardId}/competencystatus/bygroup/${groupId}`);

const getCriticalMissingCardSpecificationGapReportForGroup = (
  groupId,
  cardId,
) => Agent.get(`/card/${cardId}/competencystatus/bygroup/${groupId}/critical`);

const redirectToSpecificationGapReportForGroupAndCardId = (cardId, groupId) =>
  appService.openApiUrl(
    `/card/${cardId}/competencystatus/bygroup/${groupId}/redirect`,
  );

const getCardsByJobTitles = jobTitles =>
  Agent.get(`/card/byjobtitle`, { jobTitles });

/**
 * CardMove
 */
const createCardMove = (cardId, cardMove) =>
  Agent.post(`/card/${cardId}/cardmoves`, CardMoveMapper.to(cardMove));

const getCardMoves = cardId => Agent.get(`/card/${cardId}/cardmoves`);

/**
 * Deployment
 */
const getCardAllDeployments = cardId =>
  Agent.get(`/card/${cardId}/employee/deployments`);
const getCardOpenDeployments = cardId =>
  Agent.get(`/card/${cardId}/employee/deployments/current`);

const updateCardOpenDeployment = (cardId, deploymentDetail) =>
  Agent.post(
    `/card/${cardId}/employee/deployments/current`,
    DeploymentDetailMapper.to(deploymentDetail),
  );

const deleteDeploymentDetail = (cardId, deploymentDetailId) =>
  Agent.delete(`/card/${cardId}/employee/deployments/${deploymentDetailId}`);

/**
 * Courses
 */
const getEmployeeCourses = cardId =>
  Agent.get(`/card/${cardId}/employee/courses`);

/**
 * Work Days
 */
const getCardWorkDays = (cardId, startDate, endDate) =>
  Agent.post(`/card/${cardId}/employee/workdays/query`, {
    startDate,
    endDate,
  });

export default {
  getCard,
  getCardsForGroups,
  createCard,
  hideEmployee,
  showEmployee,
  getCardMoves,
  updateEmployee,
  createCardMove,
  updateDummyCard,
  createDummyCard,
  deleteDummyCard,
  getCardAllDeployments,
  getCardOpenDeployments,
  updateCardOpenDeployment,
  getEmployeeWorkSchedule,
  getEmployeeWorkSchedules,
  createEmployeeWorkSchedule,
  updateEmployeeWorkSchedule,
  deleteEmployeeWorkSchedule,
  getWorkScheduleDefinitions,
  getWorkScheduleOverrides,
  createWorkScheduleOverride,
  updateWorkScheduleOverride,
  deleteWorkScheduleOverride,
  getEmployeeCourses,
  queryEmployeeWorkSchedule,
  queryEmployeeWorkSchedules,
  getCardWorkDays,
  deleteDeploymentDetail,
  getCardSpecificationGapReportForGroup,
  redirectToSpecificationGapReportForGroupAndCardId,
  getCriticalMissingCardSpecificationGapReportForGroup,
  getCardsByJobTitles,
  assignProjectToEmployee,
  unassignProjectToEmployee,
  getEmployeesProjects,
};
